import React from 'react'
import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import {
  Thermometer,
  Snowflake,
  Package,
  Leaf,
  Milk,
  Warehouse,
  Truck,
  ShieldCheck,
  Activity,
  MapPin,
  Headset,
  PackageCheck,
  ClipboardCheck,
  ArrowRight,
  CheckCircle,
  Pill,
  MoveRight,
  CheckCircle2
} from 'lucide-react'

import coldChainImage from '../assets/images/data/cold-chain.png'
import CTA from './CTA'
import IndustryExpertise from './IndustryExpertise'
import MoreServicesSection from './MoreServicesSection'

const services = [
  {
    number: '01',
    icon: Pill,
    title: 'Pharmaceutical Logistics',
    description:
      'Specialized temperature-controlled transportation for medicines, vaccines, pharmaceutical products and other sensitive healthcare shipments.'
  },
  {
    number: '02',
    icon: Snowflake,
    title: 'Frozen Food Transportation',
    description:
      'Reliable refrigerated transportation solutions to maintain the required temperature and quality of frozen food products throughout transit.'
  },
  {
    number: '03',
    icon: Leaf,
    title: 'Fresh Produce Delivery',
    description:
      'Carefully managed logistics for fruits, vegetables and fresh produce to help preserve freshness and product quality.'
  },
  {
    number: '04',
    icon: Milk,
    title: 'Dairy & Beverage Logistics',
    description:
      'Temperature-conscious transportation for milk, dairy products, beverages and other perishable consumables.'
  },
  {
    number: '05',
    icon: Warehouse,
    title: 'Temperature-Controlled Warehousing',
    description:
      'Cold storage and warehousing solutions designed to support the safe handling and storage of temperature-sensitive cargo.'
  },
  {
    number: '06',
    icon: Truck,
    title: 'Reefer Truck Transportation',
    description:
      'Refrigerated vehicle transportation solutions for businesses requiring controlled environments during cargo movement.'
  }
]

const features = [
  {
    icon: Thermometer,
    title: 'Temperature Monitoring',
    description:
      'Temperature-conscious shipment handling to help maintain the required conditions throughout transportation.'
  },
  {
    icon: Snowflake,
    title: 'Reliable Cold Storage',
    description:
      'Cold storage solutions designed to protect sensitive products during storage and handling.'
  },
  {
    icon: ShieldCheck,
    title: 'Secure Transportation',
    description:
      'Careful cargo handling and reliable transportation practices to protect valuable and perishable shipments.'
  },
  {
    icon: Activity,
    title: 'Shipment Tracking',
    description:
      'Stay informed about your shipment movement with tracking visibility and timely shipment updates.'
  },
  {
    icon: MapPin,
    title: 'Pan-India Logistics Network',
    description:
      'A logistics network connecting businesses, distribution centres and destinations across India.'
  },
  {
    icon: Headset,
    title: 'Dedicated Customer Support',
    description:
      'Responsive customer assistance to help businesses coordinate shipments and resolve logistics queries.'
  }
]

const process = [
  {
    number: '01',
    icon: Package,
    title: 'Shipment Pickup',
    description:
      'We coordinate cargo pickup and carefully handle your temperature-sensitive shipments.'
  },
  {
    number: '02',
    icon: ClipboardCheck,
    title: 'Temperature Verification',
    description:
      'Shipment requirements and applicable temperature conditions are reviewed before dispatch.'
  },
  {
    number: '03',
    icon: Truck,
    title: 'Controlled Transportation',
    description:
      'Cargo moves through suitable transportation arrangements designed for temperature-sensitive goods.'
  },
  {
    number: '04',
    icon: PackageCheck,
    title: 'Safe Delivery',
    description:
      'Shipments are delivered to their destination with a focus on product quality and careful handling.'
  }
]

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
}

const sectionAnimation = {
  initial: 'hidden',
  whileInView: 'visible',
  viewport: { once: true, amount: 0.15 },
  variants: fadeUp,
  transition: { duration: 0.6 }
}

export default function ColdChainPage () {
  return (
    <>
      <Helmet>
        <title>Cold Chain Logistics | S Infinite Express</title>

        <meta
          name='description'
          content='S Infinite Express provides temperature-controlled cold chain logistics for pharmaceuticals, healthcare products, frozen foods, dairy, fresh produce and perishable shipments across India.'
        />

        <meta
          name='keywords'
          content='Cold Chain Logistics, Temperature Controlled Logistics, Refrigerated Transportation, Pharmaceutical Logistics, Frozen Food Transportation, Reefer Truck Services, Cold Storage India, Healthcare Logistics, Dairy Transportation, Fresh Produce Delivery, Perishable Goods Transportation, S Infinite Express'
        />

        <meta name='robots' content='index, follow' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/services/cold-chain'
        />

        <meta
          property='og:title'
          content='Cold Chain Logistics | S Infinite Express'
        />

        <meta
          property='og:description'
          content='Protect temperature-sensitive shipments with S Infinite Express. Explore reliable cold chain transportation, refrigerated logistics and cold storage solutions.'
        />

        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/services/cold-chain'
        />

        <meta property='og:type' content='website' />

        <meta
          property='og:image'
          content='https://sinfiniteexpress.com/static/media/cold-chain.png'
        />

        <meta property='og:site_name' content='S Infinite Express' />
      </Helmet>

      <main className='overflow-hidden bg-white'>
        {/* HERO SECTION */}
        <section className='relative overflow-hidden bg-gradient-to-br from-white via-blue-50/40 to-white'>
          <div className='max-w-7xl mx-auto px-5 sm:px-8 lg:px-6 py-8 md:py-12'>
            <div className='grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center'>
              {/* LEFT CONTENT */}
              <motion.div {...sectionAnimation} className='space-y-6'>
                <h1 className='text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-[#211f57] leading-tight'>
                  Cold Chain
                  <span className='text-[#d32c30]'> Logistics</span>
                </h1>

                <div className='w-24 h-1.5 bg-[#d32c30] rounded-full' />

                <p className='text-gray-600 text-base sm:text-lg leading-relaxed'>
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>{' '}
                  delivers reliable temperature-controlled transportation
                  solutions for businesses handling sensitive and perishable
                  goods. From pharmaceutical products and healthcare supplies to
                  frozen foods, dairy and fresh produce, we focus on maintaining
                  shipment quality through every stage of transit.
                </p>

                <p className='text-gray-600 leading-relaxed'>
                  Our cold chain logistics solutions combine dependable
                  transportation, careful cargo handling and shipment visibility
                  to help your business move temperature-sensitive products with
                  confidence.
                </p>

                {/* ACTION BUTTONS */}
                <div className='flex flex-col sm:flex-row gap-4 pt-3'>
                  <a
                    href='/get-a-quote'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#d32c30] text-white font-semibold rounded-xl shadow-lg hover:bg-[#b82629] hover:-translate-y-1 transition-all duration-300'
                  >
                    Get a Cold Chain Quote
                    <MoveRight className='w-5 h-5' />
                  </a>

                  <a
                    href='/contact-us'
                    className='inline-flex items-center justify-center gap-3 px-7 py-4 border-2 border-[#211f57] text-[#211f57] font-semibold rounded-xl hover:bg-[#211f57] hover:text-white transition-all duration-300'
                  >
                    Contact Us
                    <ArrowRight className='w-5 h-5' />
                  </a>
                </div>

                {/* TRUST POINTS */}
                <div className='flex flex-wrap gap-x-6 gap-y-3 pt-5 text-sm font-medium text-gray-600'>
                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Careful Handling
                  </span>

                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Temperature-Sensitive Cargo
                  </span>

                  <span className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 text-green-600 shrink-0' />
                    Shipment Visibility
                  </span>
                </div>
              </motion.div>

              {/* RIGHT IMAGE */}
              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -top-6 -right-5 w-32 h-32 bg-[#fff44f]/60 rounded-full blur-3xl' />
                <div className='absolute -bottom-6 -left-5 w-32 h-32 bg-blue-200/70 rounded-full blur-3xl' />

                <div className='relative p-2 bg-white rounded-3xl shadow-2xl border border-gray-100'>
                  <img
                    src={coldChainImage}
                    alt='S Infinite Express refrigerated cold chain logistics truck'
                    className='w-full h-[200px] sm:h-[300px] lg:h-[420px] object-cover rounded-2xl'
                    loading='lazy'
                    decoding='async'
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* INTRODUCTION SECTION */}
        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-8 lg:py-10'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />
          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12'>
            <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* LEFT CONTENT */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className='space-y-6'
              >
                <h2 className='text-2xl font-bold leading-tight text-[#211f57] sm:text-2xl lg:text-3xl'>
                  Protecting Sensitive Shipments Through
                  <span className='text-[#d32c30]'>
                    {' '}
                    Smarter Cold Chain Logistics
                  </span>
                </h2>

                <p className='text-base leading-relaxed text-gray-600 sm:text-lg'>
                  At{' '}
                  <span className='font-bold text-[#d32c30]'>
                    S INFINITE EXPRESS
                  </span>
                  , we understand that temperature-sensitive products require
                  specialized logistics planning and careful handling. Our cold
                  chain solutions help businesses manage the movement of
                  pharmaceuticals, healthcare products, frozen foods, dairy and
                  fresh produce.
                </p>

                <p className='leading-relaxed text-gray-600'>
                  From suitable transportation arrangements to coordinated
                  storage and delivery operations, we focus on protecting
                  product quality and supporting dependable supply chain
                  movement.
                </p>

                {/* KEY FEATURES */}
                <div className='grid grid-cols-1 gap-x-5 gap-y-4 pt-3 sm:grid-cols-2'>
                  {[
                    'Pharmaceutical & Healthcare Logistics',
                    'Frozen Food & Dairy Transportation',
                    'Fresh Fruits & Vegetables',
                    'Temperature-Controlled Solutions'
                  ].map((item, index) => (
                    <div key={index} className='flex items-center gap-3'>
                      <CheckCircle2
                        size={20}
                        className='shrink-0 text-green-600'
                      />

                      <span className='text-sm font-semibold text-[#211f57]'>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* RIGHT CAPABILITIES CARD */}
              <motion.div
                initial={{ opacity: 0, scale: 0.94, x: 30 }}
                whileInView={{ opacity: 1, scale: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className='relative'
              >
                <div className='absolute -right-5 -top-6 h-32 w-32 rounded-full bg-[#fff44f]/50 blur-3xl' />
                <div className='absolute -bottom-6 -left-5 h-32 w-32 rounded-full bg-blue-200/70 blur-3xl' />

                <div className='relative overflow-hidden rounded-3xl bg-[#211f57] p-3 shadow-2xl sm:p-4 lg:p-5'>
                  <div className='absolute -right-16 -top-16 h-48 w-48 rounded-full border-[25px] border-white/5' />
                  <div className='absolute -bottom-20 -right-10 h-56 w-56 rounded-full border-[25px] border-white/5' />

                  <div className='relative z-10'>
                    <div className='mb-2 flex items-center gap-4'>
                      <span className='text-xs font-bold uppercase tracking-[2px] text-[#fff44f]'>
                        Our Capabilities
                      </span>
                    </div>

                    <p className='mb-4 text-sm leading-7 text-white/70 sm:text-base'>
                      Integrated cold chain logistics solutions designed to
                      support sensitive cargo, preserve product quality and
                      simplify temperature-controlled transportation.
                    </p>

                    <div className='space-y-2'>
                      {[
                        {
                          icon: Pill,
                          title: 'Pharmaceutical Logistics',
                          desc: 'Specialized handling for medicines and healthcare products.'
                        },
                        {
                          icon: Snowflake,
                          title: 'Frozen & Perishable Cargo',
                          desc: 'Transportation solutions for temperature-sensitive goods.'
                        },
                        {
                          icon: Warehouse,
                          title: 'Cold Storage Solutions',
                          desc: 'Storage support for products requiring controlled conditions.'
                        },
                        {
                          icon: Truck,
                          title: 'Refrigerated Transportation',
                          desc: 'Suitable refrigerated vehicle arrangements for cargo movement.'
                        }
                      ].map((item, index) => {
                        return (
                          <div
                            key={index}
                            className='flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-all duration-300 hover:bg-white/10'
                          >
                            <div>
                              <h4 className='text-sm font-bold text-white sm:text-base'>
                                {item.title}
                              </h4>

                              <p className='mt-1 text-xs leading-5 text-white/55 sm:text-sm'>
                                {item.desc}
                              </p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* COLD CHAIN SERVICES */}
        <section className='py-8 bg-[#f7f8fc]'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='max-w-4xl mx-auto text-center mb-5'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                What We Offer
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57]'>
                Our Cold Chain Logistics Services
              </h2>

              <p className='text-gray-500 leading-7'>
                Specialized transportation and logistics solutions for
                temperature-sensitive shipments and business requirements.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {services.map((service, index) => {
                const Icon = service.icon

                return (
                  <motion.div
                    key={service.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08
                    }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='flex justify-between items-center'>
                      <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                        <Icon size={26} />
                      </div>

                      <span className='text-3xl font-black text-gray-100 group-hover:text-[#d32c30]/20 transition-colors'>
                        {service.number}
                      </span>
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-3'>
                      {service.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {service.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* INDUSTRY EXPERTISE */}
        <IndustryExpertise />

        {/* WHY CHOOSE US */}
        <section className='relative overflow-hidden bg-gradient-to-br from-yellow-50 via-blue-50/40 to-yellow-100/60 py-8 sm:py-10 lg:py-12'>
          <div className='absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[#fff44f]/10 blur-[90px]' />
          <div className='absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-blue-200/30 blur-[90px]' />

          <div className='relative max-w-7xl mx-auto px-4'>
            <div className='text-center max-w-3xl mx-auto mb-4'>
              <span className='text-[#d32c30] font-extrabold uppercase tracking-[2px] text-xs'>
                The S Infinite Advantage
              </span>

              <h2 className='text-3xl sm:text-4xl font-extrabold text-[#211f57]'>
                Why Choose Our Cold Chain Logistics?
              </h2>

              <p className='text-gray-500 leading-7'>
                Dependable logistics solutions focused on careful handling,
                suitable transportation and shipment visibility.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {features.map((feature, index) => {
                const Icon = feature.icon

                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.08
                    }}
                    className='group bg-white p-7 rounded-2xl border border-gray-100 hover:border-[#d32c30]/30 hover:shadow-xl transition-all duration-300'
                  >
                    <div className='w-14 h-14 rounded-xl bg-[#211f57] group-hover:bg-[#d32c30] flex items-center justify-center text-white transition-colors'>
                      <Icon size={26} />
                    </div>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-4'>
                      {feature.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-2'>
                      {feature.description}
                    </p>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* PROCESS SECTION */}
        <section className='py-8 lg:py-10 bg-white'>
          <div className='max-w-7xl mx-auto px-6'>
            <div className='text-center max-w-4xl mx-auto mb-5'>
              <span className='text-[#d32c30] font-extrabold text-xs uppercase tracking-[2px]'>
                How It Works
              </span>

              <h2 className='text-2xl sm:text-3xl font-extrabold text-[#211f57]'>
                Our Cold Chain Logistics Process
              </h2>

              <p className='text-gray-500 leading-7'>
                A structured transportation process focused on protecting
                temperature-sensitive shipments at every stage.
              </p>
            </div>

            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-8 relative'>
              {process.map((step, index) => {
                const Icon = step.icon

                return (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 25 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.5,
                      delay: index * 0.1
                    }}
                    className='relative text-center'
                  >
                    <div className='w-16 h-16 mx-auto rounded-2xl bg-[#211f57] text-white flex items-center justify-center shadow-lg'>
                      <Icon size={27} />
                    </div>

                    <span className='inline-block text-xs font-extrabold text-[#d32c30] mt-4'>
                      STEP {step.number}
                    </span>

                    <h3 className='text-lg font-extrabold text-[#211f57] mt-2'>
                      {step.title}
                    </h3>

                    <p className='text-sm text-gray-500 leading-7 mt-3'>
                      {step.description}
                    </p>

                    {index !== process.length - 1 && (
                      <div className='hidden lg:block absolute top-8 left-[calc(50%+45px)] w-[calc(100%-50px)] border-t-2 border-dashed border-[#d32c30]/30' />
                    )}
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className='relative bg-[#211f57] py-4 overflow-hidden'>
          <div className='absolute right-0 top-0 w-96 h-96 rounded-full border-[50px] border-white/5 translate-x-1/3 -translate-y-1/3' />

          <div className='relative max-w-6xl mx-auto px-6 text-center'>
            <h2 className='text-2xl sm:text-3xl lg:text-3xl font-extrabold text-white mt-2 leading-tight'>
              Looking for a Reliable
              <span className='block text-[#FFF44F]'>
                Cold Chain Logistics Partner?
              </span>
            </h2>

            <p className='text-white/70 max-w-6xl mx-auto leading-8'>
              Let S Infinite Express support your temperature-sensitive cargo
              requirements with careful handling and dependable cold chain
              logistics solutions.
            </p>
          </div>
        </section>

        {/* MORE SERVICES */}
        <MoreServicesSection />

        {/* COMMON CTA */}
        <CTA />
      </main>
    </>
  )
}
